/**
 * The setback envelope: the no-panel zone a permit review will look for (SD-4.3, FR-10, AD-21).
 *
 * A rep has to be able to trust that what they are showing a customer will survive plan check.
 * This module turns the tenant's three configured insets into the region panels are allowed to
 * occupy, per face, and it is the same geometry the packer will consume — the envelope drawn on
 * the canvas is not a decoration that approximates the rule, it IS the rule.
 *
 * ── THREE INSETS, ONE FACE ───────────────────────────────────────────────────────────────────
 * Ridge, eave and rake each have their own configured value and they apply to DIFFERENT EDGES OF
 * THE SAME POLYGON. That is why this uses SD-4.2's `offsetPolygonPerEdge` rather than a single
 * uniform inset: a uniform buffer would silently apply the eave number to the ridge, and on a
 * shallow roof with a large eave setback that is the difference between a layout that passes and
 * one that does not.
 *
 * ── `unknown` TAKES THE MOST CONSERVATIVE VALUE, AND THIS IS LOAD-BEARING ─────────────────────
 * SD-1.3 derives edge classification from geometry because Google returns none, and it labels an
 * edge `unknown` whenever the derivation is not confident. Many edges on a real roof are
 * `unknown` — the corpus's Oak Park address is mostly unknown edges. Guessing small there would
 * place panels into a zone that may be a real setback; guessing large costs a little roof area on
 * an estimate that is already labelled an estimate. So `unknown` takes the MAXIMUM of the three
 * configured insets, and `valley` and `hip` do too, for the same reason: neither is one of the
 * three the tenant configured, so neither has a value of its own to use.
 *
 * ── UNITS ────────────────────────────────────────────────────────────────────────────────────
 * Settings are in INCHES (that is what a code official and an installer both speak) and the roof
 * model is in METRES. The conversion happens once, here, at the boundary. A setback value that
 * reaches the geometry in inches would be ~39x too large and would erase most roofs, which is the
 * kind of bug that looks like "the packer is broken".
 */

import type { EdgeKind, RoofModelV1 } from '@/types/solar/roofModel';
import { offsetPolygonPerEdge } from './geometry/offset';
import type { Pt } from './geometry/types';

export const INCHES_PER_METRE = 39.3700787;

export interface SetbackInches {
  ridge: number;
  eave: number;
  rake: number;
}

/**
 * Which configured inset an edge kind uses.
 *
 * `valley`, `hip` and `unknown` deliberately have no entry: they resolve to the conservative
 * maximum in `insetForEdgeMetres`. Listing them here with a guessed value is exactly the mistake
 * this table's absence prevents.
 */
const EDGE_TO_SETBACK: Partial<Record<EdgeKind, keyof SetbackInches>> = {
  ridge: 'ridge',
  eave: 'eave',
  rake: 'rake',
};

export function conservativeInches(setbacks: SetbackInches): number {
  return Math.max(setbacks.ridge, setbacks.eave, setbacks.rake);
}

/** The inset for one edge, in METRES. */
export function insetForEdgeMetres(kind: EdgeKind, setbacks: SetbackInches): number {
  const key = EDGE_TO_SETBACK[kind];
  const inches = key ? setbacks[key] : conservativeInches(setbacks);
  return Math.max(0, inches) / INCHES_PER_METRE;
}

/**
 * Per-edge inset distances for a face, in the polygon's own vertex order.
 *
 * `edges` carries `from_index`, so the distance array is built by INDEX rather than by iterating
 * the edge list in order — the producer is not required to emit edges in vertex order, and a
 * positional assumption would silently rotate every setback around the roof.
 *
 * An edge the model does not describe falls back to the conservative value rather than to zero.
 * Zero would be a hole in the envelope precisely where the data is thinnest.
 */
export function edgeInsetsMetres(
  face: Pick<RoofModelV1['planes'][number], 'polygon_m' | 'edges'>,
  setbacks: SetbackInches
): number[] {
  const n = face.polygon_m?.length ?? 0;
  const fallback = conservativeInches(setbacks) / INCHES_PER_METRE;
  const out = new Array<number>(n).fill(fallback);

  for (const edge of face.edges ?? []) {
    const i = edge.from_index;
    if (Number.isInteger(i) && i >= 0 && i < n) {
      out[i] = insetForEdgeMetres(edge.kind, setbacks);
    }
  }
  return out;
}

export interface FaceEnvelope {
  planeId: string;
  /**
   * The placeable regions. PLURAL, and never collapsed to the first.
   *
   * Offsetting a concave face can legitimately pinch it into several disjoint areas — a dormered
   * roof is the ordinary case, not the exotic one. Rendering only `regions[0]` would draw an
   * envelope that disagrees with where the packer is allowed to put panels, and would do so
   * silently on exactly the roofs where the setback matters most.
   */
  regions: Pt[][];
}

/** The envelope for every face that has one. */
export function setbackEnvelope(
  model: Pick<RoofModelV1, 'planes'> | null | undefined,
  setbacks: SetbackInches
): FaceEnvelope[] {
  const out: FaceEnvelope[] = [];
  for (const plane of model?.planes ?? []) {
    const polygon = plane.polygon_m;
    if (!polygon || polygon.length < 3) continue;

    const regions = offsetPolygonPerEdge(
      polygon as readonly Pt[],
      edgeInsetsMetres(plane, setbacks)
    );
    // A face can be entirely consumed by its own setbacks — a small dormer with a large eave
    // inset. That is a real answer, not an error: the face simply has no placeable area. It is
    // omitted rather than emitted with an empty region list, so consumers never have to
    // distinguish "no envelope" from "an envelope of nothing".
    if (regions.length > 0) out.push({ planeId: plane.id, regions });
  }
  return out;
}

/** Every region across every face, for rendering. */
export function envelopeRegions(envelopes: readonly FaceEnvelope[]): Pt[][] {
  return envelopes.flatMap((e) => e.regions);
}
